import { PfApiError, request } from "pages/PayRoll/Configuration/StatutoryConfiguration/PF/pfConfigApi";

// Administration > Sites APIs (api/administration/sites/). Same fetch
// helper as the PF screens: token auth, 401 logout, {data, message}
// responses and field errors on rejected saves (error.fieldErrors).

const BASE = "administration/sites/";

const payload = (data) => {
  if (!data || typeof data !== "object" || !("data" in data)) throw new PfApiError("The server returned an invalid response.");
  return data.data;
};
const call = (options) => request({ base: BASE, ...options });

// List filters -> query params (blank values are left out).
export const listQuery = ({ search, status, siteType, state, companyId, ordering, page, pageSize } = {}) => {
  const query = {};
  const add = (key, value) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") query[key] = String(value).trim();
  };
  add("search", search);
  add("status", status);
  add("site_type", siteType);
  add("state", state);
  add("company_id", companyId);
  add("ordering", ordering);
  add("page", page);
  add("page_size", pageSize);
  return query;
};

export const getSites = async ({ domain, signal, ...filters }) =>
  payload(await call({ domain, path: "", query: listQuery(filters), signal }));

export const getSiteSummary = async ({ domain, signal }) => payload(await call({ domain, path: "summary/", signal }));

export const getNextSiteCode = async ({ domain, signal }) => payload(await call({ domain, path: "next-code/", signal })).code;

export const getSite = async ({ domain, id, signal }) => payload(await call({ domain, path: `${id}/`, signal }));

export const createSite = async ({ domain, body }) => {
  const data = await call({ domain, path: "", method: "POST", body });
  return { site: payload(data), message: data.message };
};

export const updateSite = async ({ domain, id, body }) => {
  const data = await call({ domain, path: `${id}/`, method: "PATCH", body });
  return { site: payload(data), message: data.message };
};

export const activateSite = async ({ domain, id }) => {
  const data = await call({ domain, path: `${id}/activate/`, method: "POST" });
  return { site: payload(data), message: data.message };
};

export const deactivateSite = async ({ domain, id }) => {
  const data = await call({ domain, path: `${id}/deactivate/`, method: "POST" });
  return { site: payload(data), message: data.message };
};

export const moveSiteEmployees = async ({ domain, id, targetSiteId }) =>
  payload(await call({ domain, path: `${id}/move-employees/`, method: "POST", body: { target_site_id: targetSiteId } }));

export const deleteSite = async ({ domain, id }) => payload(await call({ domain, path: `${id}/`, method: "DELETE" }));

// xlsx of the list with the same filters ({ blob, fileName }).
export const exportSites = ({ domain, ...filters }) =>
  call({ domain, path: "export/", query: listQuery({ ...filters, page: undefined, pageSize: undefined }), responseType: "blob" });

// {state, district, local_body} of a site with this PIN, or {}.
export const lookupPincode = async ({ domain, pin, signal }) =>
  payload(await call({ domain, path: `lookup/pincode/${encodeURIComponent(pin)}/`, signal }));

// Lists may come back plain or wrapped ({ results } / { <name>: [...] }).
export const rowsOf = (data, key) => {
  if (Array.isArray(data)) return data;
  const rows = data?.results ?? data?.[key];
  return Array.isArray(rows) ? rows : [];
};

export const getDistricts = async ({ domain, state, signal }) =>
  rowsOf(payload(await call({ domain, path: "districts/", query: { state }, signal })), "districts");

export const createDistrict = async ({ domain, state, name }) =>
  payload(await call({ domain, path: "districts/", method: "POST", body: { state, name } }));

export const getLocalBodies = async ({ domain, districtId, signal }) =>
  rowsOf(payload(await call({ domain, path: "local-bodies/", query: { district_id: districtId }, signal })), "local_bodies");

export const createLocalBody = async ({ domain, districtId, name, bodyType }) =>
  payload(await call({ domain, path: "local-bodies/", method: "POST", body: { district_id: districtId, name, body_type: bodyType } }));

// Active sites [{id, name, code}] for onboarding / employee edit.
export const getSiteOptions = async ({ domain, signal }) => payload(await call({ domain, path: "options/", signal }));

// Companies for the list's Company filter (existing companies API).
export const getCompanies = async ({ domain, signal }) => {
  const data = await request({ domain, base: "", path: "company/", signal });
  const rows = Array.isArray(data) ? data : data?.results || data?.data || [];
  return rows.map((company) => ({ id: company.id, name: company.companyname || company.name || "" }));
};

// Saves a downloaded blob as a file.
export const saveBlob = ({ blob, fileName }, fallbackName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
